import React, { useState, useRef, useEffect } from 'react';
import '../style/services.css';

import { FaPlay, FaPause, FaMagic, FaFilm, FaVideo, FaCamera, FaHeart, FaStar, FaRocket, FaGlobe, FaAward } from 'react-icons/fa';
import ContactUs from './contactus';
import background from '../../assets/servicebg.mp4';

const Services = () => {
    const [isVideoPlaying, setIsVideoPlaying] = useState(true);
    const videoRef = useRef(null);

    // Set playback rate when component mounts and video is ready
    useEffect(() => {
        const setVideoPlaybackRate = () => {
            if (videoRef.current) {
                videoRef.current.playbackRate = 0.55;
            }
        };

        // Set playback rate immediately if video is already loaded
        if (videoRef.current) {
            setVideoPlaybackRate();
        }

        // Also set up event listener for when video metadata loads
        const videoElement = videoRef.current;
        if (videoElement) {
            videoElement.addEventListener('loadedmetadata', setVideoPlaybackRate);
            videoElement.addEventListener('play', setVideoPlaybackRate);
        }

        // Cleanup event listeners
        return () => {
            if (videoElement) {
                videoElement.removeEventListener('loadedmetadata', setVideoPlaybackRate);
                videoElement.removeEventListener('play', setVideoPlaybackRate);
            }
        };
    }, []);

    const servicesData = [
        {
            id: 1,
            title: "VFX & HYPERLAPSE",
            description: "Transform ordinary footage into extraordinary visual experiences with cutting-edge VFX and hyperlapse techniques that captivate your audience.",
            icon: <FaMagic />,
            features: ["Motion Graphics", "3D Integration", "Smooth Transitions"],
            shortContent: "Cinematic visual effects and time-lapse magic that transforms reality into extraordinary visual storytelling."
        },
        {
            id: 2,
            title: "MOTION GRAPHICS",
            description: "Master the art of pacing with speed ramping, stop motion, and match cuts that create emotional impact and visual rhythm.",
            icon: <FaFilm />,
            features: ["Speed Ramping", "Stop Motion", "Match Cuts"],
            shortContent: "Dynamic editing techniques that create rhythm, emotion, and seamless visual flow in every frame."
        },
        {
            id: 3,
            title: "VIDEO PRODUCTION",
            description: "From concept to delivery, we produce cinematic video content that tells your story with professional quality and creative vision.",
            icon: <FaVideo />,
            features: ["4K Production", "Multi-camera", "Cinematic Quality"],
            shortContent: "End-to-end production services delivering professional, cinematic content from concept to final cut."
        },
        {
            id: 4,
            title: "EVENT COVERAGE",
            description: "Capture the energy and emotion of your events with comprehensive coverage that preserves every important moment.",
            icon: <FaCamera />,
            features: ["Live Coverage", "Multi-angle", "Quick Delivery"],
            shortContent: "Comprehensive event documentation capturing every moment with multiple angles and professional equipment."
        },
        {
            id: 5,
            title: "WEDDING FILMS",
            description: "Transform your special day into a cinematic love story that captures emotions, details, and magical moments forever.",
            icon: <FaHeart />,
            features: ["Emotional Story", "Drone Shots", "Heirloom Quality"],
            shortContent: "Cinematic wedding storytelling that preserves your special day as an emotional, timeless visual treasure."
        }
    ];

    const floatingElements = [
        { icon: <FaStar />, position: { top: '20%', left: '60%' } },
        { icon: <FaRocket />, position: { top: '40%', left: '80%' } },
        { icon: <FaGlobe />, position: { top: '60%', left: '70%' } },
        { icon: <FaAward />, position: { top: '30%', left: '85%' } },
        { icon: <FaMagic />, position: { top: '70%', left: '65%' } }
    ];

    const toggleVideoPlayback = () => {
        if (videoRef.current) {
            if (isVideoPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
                videoRef.current.playbackRate = 0.55;
            }
            setIsVideoPlaying(!isVideoPlaying);
        }
    };

    return (
        <div className="fx-services-container">
            {/* Section 1: Full Width Video */}
            <section className="fx-video-container">
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="fx-background-video"
                >
                    <source src={background} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                
                <div className="fx-video-overlay">
                    <div className="fx-video-text">
                        <h1 className="fx-video-heading">OUR SERVICES</h1>
                    </div>
                    
                    <button 
                        className="fx-video-control"
                        onClick={toggleVideoPlayback}
                    >
                        {isVideoPlaying ? <FaPause /> : <FaPlay />}
                    </button>
                </div>
            </section>

            {/* Section 2: Hero Content */}
            <section className="fx-services-hero">
                <div className="fx-hero-wrapper">
                    <div className="fx-hero-text">
                        <h2 className="fx-hero-title">
                            Creative Visual
                            <span className="fx-gradient-text"> Solutions</span>
                        </h2>
                        <p className="fx-hero-description">
                            We transform your vision into breathtaking visual experiences through cutting-edge 
                            technology and artistic innovation. Our comprehensive services cover every aspect 
                            of modern visual production.
                        </p>
                        <div className="fx-stats-container">
                            <div className="fx-stat-item">
                                <span className="fx-stat-number">500+</span>
                                <span className="fx-stat-label">Projects</span>
                            </div>
                            <div className="fx-stat-item">
                                <span className="fx-stat-number">50+</span>
                                <span className="fx-stat-label">Clients</span>
                            </div>
                            <div className="fx-stat-item">
                                <span className="fx-stat-number">99%</span>
                                <span className="fx-stat-label">Satisfaction</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="fx-hero-graphics">
                        <div className="fx-floating-elements">
                            {floatingElements.map((item, index) => (
                                <div 
                                    key={index}
                                    className="fx-floating-element"
                                    style={item.position}
                                >
                                    {item.icon}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Section 3: Services Grid */}
            <section className="fx-services-grid-section">
                <div className="fx-services-wrapper">
                    <div className="fx-grid-row">
                        {/* First Row - 3 Cards */}
                        {servicesData.slice(0, 3).map((service) => (
                            <div key={service.id} className="fx-service-item">
                                <div className="fx-service-icon">
                                    {service.icon}
                                </div>
                                <h3 className="fx-service-name">{service.title}</h3>
                                <p className="fx-service-desc">{service.description}</p>
                                <div className="fx-service-tags">
                                    {service.features.map((feature, idx) => (
                                        <span key={idx} className="fx-tag-item">
                                            {feature}
                                        </span>
                                    ))}
                                </div>
                                <div className="fx-short-content">
                                    {service.shortContent}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="fx-grid-row">
                        {/* Second Row - 2 Cards */}
                        {servicesData.slice(3, 5).map((service) => (
                            <div key={service.id} className="fx-service-item">
                                <div className="fx-service-icon">
                                    {service.icon}
                                </div>
                                <h3 className="fx-service-name">{service.title}</h3>
                                <p className="fx-service-desc">{service.description}</p>
                                <div className="fx-service-tags">
                                    {service.features.map((feature, idx) => (
                                        <span key={idx} className="fx-tag-item">
                                            {feature}
                                        </span>
                                    ))}
                                </div>
                                <div className="fx-short-content">
                                    {service.shortContent}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section 4: Contact Us */}
            <section className="fx-contact-section">
                <ContactUs />
            </section>
        </div>
    );
};

export default Services;